import React, { useState, useMemo } from 'react';
import './SummaryDashboard.scss';
import { PedidoModification } from '../../../../Types';
import { 
  ModificationData, 
  UserStats,
  ItemStats,
  calculateModificationAnalytics, 
  formatCurrency 
} from '../../../../utils/ModificationAnalytics';
import { 
  FaArrowUp, 
  FaArrowDown, 
  FaDollarSign, 
  FaHashtag, 
  FaTriangleExclamation,
  FaExclamation
} from 'react-icons/fa6';

interface SummaryDashboardProps {
  modifications: PedidoModification[];
  startDate: string; // e.g. "2025-05-01"
  endDate: string;   // e.g. "2025-05-31"
}

const MAX_VISIBLE = 3; // show only first 3 users initially

const SummaryDashboard: React.FC<SummaryDashboardProps> = ({
  modifications,
  startDate,
  endDate,
}) => {
  const [showAllNegative, setShowAllNegative] = useState<boolean>(false);
  const [showAllItems, setShowAllItems] = useState<boolean>(false);
  
  // Calculate analytics from real data
  const analytics = useMemo(() => {
    if (!modifications || modifications.length === 0) return null;
    // Convert PedidoModification to ModificationData format
    const modificationData: ModificationData[] = modifications.map(mod => ({
      ...mod,
      mesa_id: mod.mesa_id || 0, // Default to 0 if not provided
      workstation_id: mod.workstation_id || null, // Keep as null if not provided
      modification_metadata: mod.modification_metadata || {}, // Default to empty object
      approver_id: mod.user_id, // Assume user is their own approver if not provided
      session_id: null, // Default value for missing field
    }));
    return calculateModificationAnalytics(modificationData, startDate, endDate);
  }, [modifications, startDate, endDate]);

  if (!analytics) {
    return (
      <div className="summary-dashboard-container">
        <div className="no-data-message">
          <FaExclamation />
          <p>No hay datos de modificaciones para mostrar</p>
        </div>
      </div>
    );
  }

  // Helper: get two‐letter initials
  const getAvatarLetters = (name: string) => {
    const parts = name.trim().split(' ');
    if (parts.length === 1) {
      return parts[0].slice(0, 2).toUpperCase();
    }
    return (
      parts[0][0].toUpperCase() + parts[parts.length - 1][0].toUpperCase()
    );
  };

  // Decide which slice of users to show
  const displayedNegativeUsers = showAllNegative
    ? analytics.top_negative_users
    : analytics.top_negative_users.slice(0, MAX_VISIBLE);
    
  const displayedItems = showAllItems
    ? analytics.most_modified_items
    : analytics.most_modified_items.slice(0, MAX_VISIBLE);

  // Calculate today vs average comparison
  const dailyAvgNegativeCount = analytics.daily_summary.length > 0 
    ? analytics.negative_modifications / analytics.daily_summary.length 
    : 0;
  const deltaCount = analytics.today_negative_count - dailyAvgNegativeCount;
  const deltaAmount = analytics.today_negative_amount - analytics.avg_daily_negative_impact;

  // Determine if positive (today > avg) or negative (today < avg)
  const isDeltaCountPositive = deltaCount > 0;
  const isDeltaAmountPositive = deltaAmount > 0;

  // Format Δ: show absolute value with two decimals
  const formatDelta = (value: number): string => {
    return Math.abs(value).toFixed(2);
  };
  
  // Calculate negative impact percentage
  const negativeImpactPercentage = analytics.total_modifications > 0 
    ? (analytics.negative_modifications / analytics.total_modifications) * 100 
    : 0;
    
  // Get variation class
  const getVariationClass = (isPositive: boolean) => {
    return isPositive ? 'positive-variation' : 'negative-variation';
  };



  return (
    <div className="summary-dashboard-container">
      {/* Main KPI Cards Row */}
      <div className="kpi-cards-row">
        {/* Total Modifications */}
        <div className="kpi-card total-mods">
          <div className="kpi-card-header">
            <div className="kpi-icon">
              <FaHashtag />
            </div>
            <div className="kpi-title">Total Modificaciones</div>
          </div>
          <div className="kpi-value">{analytics.total_modifications.toLocaleString()}</div>
          <div className="kpi-subtitle">
            {analytics.daily_summary.length} días analizados
          </div>
        </div>

        {/* Negative Impact Rate */}
        <div className="kpi-card negative-impact">
          <div className="kpi-card-header">
            <div className="kpi-icon warning">
              <FaTriangleExclamation />
            </div>
            <div className="kpi-title">Impacto Negativo</div>
          </div>
          <div className="kpi-value">{negativeImpactPercentage.toFixed(1)}%</div>
          <div className="kpi-subtitle">
            {analytics.negative_modifications} de {analytics.total_modifications} modificaciones
          </div>
        </div>

        {/* Financial Impact */}
        <div className="kpi-card financial-impact">
          <div className="kpi-card-header">
            <div className="kpi-icon financial">
              <FaDollarSign />
            </div>
            <div className="kpi-title">Impacto Financiero</div>
          </div>
          <div className="kpi-value">{formatCurrency(analytics.total_negative_impact)}</div>
          <div className="kpi-subtitle">
            Solo modificaciones negativas - Promedio: {formatCurrency(analytics.avg_daily_negative_impact)}/día
          </div>
        </div>

      </div>

      {/* Today's Performance vs Average */}
      <div className="today-performance-card">
        <div className="performance-header">
          <h3>Rendimiento de Hoy vs Promedio</h3>
          <div className="performance-date">{new Date().toLocaleDateString('es-ES')}</div>
        </div>
        <div className="performance-metrics">
          <div className="performance-metric">
            <div className="metric-label"> Negativas</div>
            <div className="metric-value-container">
              <div className="metric-value">{analytics.today_negative_count}</div>
              {deltaCount !== 0 && (
                <div className={`metric-variation ${getVariationClass(isDeltaCountPositive)}`}>
                  {isDeltaCountPositive ? <FaArrowUp /> : <FaArrowDown />}
                  {formatDelta(deltaCount)}
                </div>
              )}
            </div>
            <div className="metric-comparison">
              Promedio: {dailyAvgNegativeCount.toFixed(1)}/día
            </div>
          </div>
          
          <div className="performance-metric">
            <div className="metric-label">Impacto Financiero</div>
            <div className="metric-value-container">
              <div className="metric-value">{formatCurrency(analytics.today_negative_amount)}</div>
              {deltaAmount !== 0 && (
                <div className={`metric-variation ${getVariationClass(isDeltaAmountPositive)}`}>
                  {isDeltaAmountPositive ? <FaArrowUp /> : <FaArrowDown />}
                  {formatCurrency(Math.abs(deltaAmount))}
                </div>
              )}
            </div>
            <div className="metric-comparison">
              Promedio: {formatCurrency(analytics.avg_daily_negative_impact)}/día
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Row: Rankings and Analysis */}
      <div className="analysis-row">
        {/* Top Negative Users */}
        <div className="analysis-card users-ranking">
          <div className="analysis-card-header">
            <h4>
              <FaTriangleExclamation className="header-icon" />
              Usuarios con Más Modificaciones Negativas
            </h4>
            <span className="analysis-badge">Top {MAX_VISIBLE}</span>
          </div>
          <div className="ranking-list">
            {displayedNegativeUsers.length > 0 ? (
              displayedNegativeUsers.map((user: UserStats, idx: number) => (
                <div key={idx} className="ranking-item">
                  <div className="ranking-position">{idx + 1}</div>
                  <div className="ranking-details">
                    <div className="ranking-info">
                      <div className="ranking-name-row">
                        <div className="ranking-avatar">
                          {getAvatarLetters(user.user_name)}
                        </div>
                        <div className="ranking-name">{user.user_name}</div>
                        <div className="stat-amount">{formatCurrency(user.total_negative_amount)}</div>
                      </div>
                      <div className="ranking-stats-row">
                        <span className="stat-count">{user.negative_count} modificaciones</span>
                        <div className="ranking-percentage">
                          {((user.negative_count / analytics.negative_modifications) * 100).toFixed(1)}%
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-data">Sin datos disponibles</div>
            )}
          </div>
          {analytics.top_negative_users.length > MAX_VISIBLE && (
            <button
              className="show-more-btn"
              onClick={() => setShowAllNegative(!showAllNegative)}
            >
              {showAllNegative ? 'Mostrar menos' : `Ver todos (${analytics.top_negative_users.length})`}
            </button>
          )}
        </div>

        {/* Most Modified Items */}
        <div className="analysis-card items-analysis">
          <div className="analysis-card-header">
            <h4>
              <FaExclamation className="header-icon" />
              Productos Más Problemáticos
            </h4>
            <span className="analysis-badge">Por Impacto Negativo</span>
          </div>
          <div className="items-list">
            {displayedItems.length > 0 ? (
              displayedItems.map((item: ItemStats, idx: number) => (
                <div key={idx} className="item-row">
                  <div className="item-details">
                    <div className="item-info">
                      <div className="item-name-row">
                        <div className="item-position">{idx + 1}</div>
                        <div className="item-name">{item.item_name}</div>
                        <div className="impact-amount">{formatCurrency(item.total_amount_impact)}</div>
                      </div>
                      <div className="item-stats-row">
                        <span className="item-count">
                          <FaHashtag className="stat-icon" />
                          {item.modification_count} modificaciones
                        </span>
                        <div className="impact-avg">
                          Promedio: {formatCurrency(item.negative_count > 0 ? item.total_amount_impact / item.negative_count : 0)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-data">Sin datos disponibles</div>
            )}
          </div>
          {analytics.most_modified_items.length > MAX_VISIBLE && (
            <button
              className="show-more-btn"
              onClick={() => setShowAllItems(!showAllItems)}
            >
              {showAllItems ? 'Mostrar menos' : `Ver todos (${analytics.most_modified_items.length})`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SummaryDashboard;
